import React from 'react';
import { parkingLocations } from '../../../mocks/transportationPage';

export default function ParkingSection() {
  return (
    <section id="parking" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-12">
          <span className="inline-block bg-teal-100 text-teal-700 px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wide mb-4">
            Parking
          </span>
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Parking in Roseville</h2>
          <p className="text-gray-600 max-w-2xl mx-auto text-sm">
            Most parking in Roseville is free. Find garages, surface lots, and street parking throughout the city.
          </p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 text-center">
            <div className="w-12 h-12 flex items-center justify-center bg-teal-50 rounded-full mx-auto mb-3">
              <i className="ri-parking-box-line text-2xl text-teal-600"></i>
            </div>
            <p className="text-3xl font-bold text-gray-900">7,400+</p>
            <p className="text-gray-500 text-sm">Public Spaces</p>
          </div>

          <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 text-center">
            <div className="w-12 h-12 flex items-center justify-center bg-green-50 rounded-full mx-auto mb-3">
              <i className="ri-money-dollar-circle-line text-2xl text-green-600"></i>
            </div>
            <p className="text-3xl font-bold text-gray-900">Free</p>
            <p className="text-gray-500 text-sm">Most Locations</p>
          </div>

          <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 text-center">
            <div className="w-12 h-12 flex items-center justify-center bg-amber-50 rounded-full mx-auto mb-3">
              <i className="ri-charging-pile-2-line text-2xl text-amber-600"></i>
            </div>
            <p className="text-3xl font-bold text-gray-900">50+</p>
            <p className="text-gray-500 text-sm">EV Chargers</p>
          </div>

          <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 text-center">
            <div className="w-12 h-12 flex items-center justify-center bg-rose-50 rounded-full mx-auto mb-3">
              <i className="ri-wheelchair-line text-2xl text-rose-500"></i>
            </div>
            <p className="text-3xl font-bold text-gray-900">ADA</p>
            <p className="text-gray-500 text-sm">Accessible</p>
          </div>
        </div>

        {/* Parking Locations */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {parkingLocations.map((location) => (
            <div
              key={location.id ?? Math.random().toString(36).substr(2, 9)}
              className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 hover:shadow-lg hover:border-teal-200 transition-all group"
            >
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 flex items-center justify-center bg-teal-50 rounded-xl group-hover:bg-teal-100 transition-colors flex-shrink-0">
                  {/* Ensure location.icon is a valid string; fallback to a generic icon */}
                  <i className={`${location.icon || 'ri-parking-box-line'} text-2xl text-teal-600`}></i>
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between mb-1">
                    <h4 className="font-semibold text-gray-900 group-hover:text-teal-600 transition-colors">
                      {location.name ?? 'Unnamed Location'}
                    </h4>
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full whitespace-nowrap ml-2">
                      {location.type ?? 'Unknown'}
                    </span>
                  </div>

                  <p className="text-gray-500 text-sm mb-3 flex items-center space-x-1">
                    <i className="ri-map-pin-2-line"></i>
                    <span>{location.address ?? 'No address provided'}</span>
                  </p>

                  <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                    <div className="flex items-center space-x-1 text-gray-600">
                      <i className="ri-car-line"></i>
                      <span>{location.spaces != null ? `${location.spaces} spaces` : 'N/A'}</span>
                    </div>
                    <div className="flex items-center space-x-1 text-gray-600">
                      <i className="ri-time-line"></i>
                      <span>{location.hours ?? 'Hours unknown'}</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-teal-600 font-semibold text-sm">{location.rate ?? 'Free'}</span>
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                        `${location.address ?? ''}, Roseville, CA`
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-gray-500 hover:text-teal-600 flex items-center space-x-1 cursor-pointer"
                    >
                      <i className="ri-direction-line"></i>
                      <span>Directions</span>
                    </a>
                  </div>

                  {/* Features – render only if an array is provided */}
                  {Array.isArray(location.features) && location.features.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-100 flex flex-wrap gap-1">
                      {location.features.map((feature, idx) => (
                        <span
                          key={idx}
                          className="text-xs bg-teal-50 text-teal-700 px-2 py-1 rounded-full"
                        >
                          {feature}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
